import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'semStupenkiProgress';

const initialProgress = {
  parentName: '',
  childName: '',
  childAge: null,
  isOnboarded: false,
  currentBlockId: 1,
  currentLessonId: 1,
  completedLessons: [],
  lessonAccuracies: {}, // { lessonId: bestAccuracy }
  streakDays: 0,
  lastSessionDate: null,
  totalExercisesCompleted: 0
};

export const useProgress = () => {
  const [progress, setProgress] = useState(initialProgress);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setProgress({ ...initialProgress, ...parsed });
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    setIsLoaded(true);
  }, []);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
      } catch (error) {
        console.error('Error saving progress:', error);
      }
    }
  }, [progress, isLoaded]);

  // Complete onboarding
  const completeOnboarding = useCallback((parentName, childName, childAge) => {
    setProgress(prev => ({
      ...prev,
      parentName,
      childName,
      childAge,
      isOnboarded: true
    }));
  }, []);

  // Complete a lesson
  const completeLesson = useCallback((lessonId, accuracy) => {
    setProgress(prev => {
      const newCompletedLessons = prev.completedLessons.includes(lessonId)
        ? prev.completedLessons
        : [...prev.completedLessons, lessonId];
      
      const newAccuracies = {
        ...prev.lessonAccuracies,
        [lessonId]: Math.max(prev.lessonAccuracies[lessonId] || 0, accuracy)
      };

      // Update streak
      const today = new Date().toDateString();
      const lastDate = prev.lastSessionDate;
      let newStreak = prev.streakDays;
      
      if (lastDate !== today) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        if (lastDate === yesterday.toDateString()) {
          newStreak = prev.streakDays + 1;
        } else if (lastDate !== today) {
          newStreak = 1;
        }
      }

      // Determine next lesson/block
      let nextLessonId = prev.currentLessonId;
      let nextBlockId = prev.currentBlockId;
      
      if (accuracy >= 85) {
        nextLessonId = lessonId + 1;
        // Check if we need to move to next block
        // This is simplified - in production would check lesson's blockId
        if (lessonId === 3) nextBlockId = 2;
        else if (lessonId === 6) nextBlockId = 3;
        else if (lessonId === 9) nextBlockId = 4;
        else if (lessonId === 12) nextBlockId = 5;
      }

      return {
        ...prev,
        completedLessons: newCompletedLessons,
        lessonAccuracies: newAccuracies,
        currentLessonId: nextLessonId,
        currentBlockId: nextBlockId,
        streakDays: newStreak,
        lastSessionDate: today,
        totalExercisesCompleted: prev.totalExercisesCompleted + 5
      };
    });
  }, []);

  // Check if a lesson is unlocked
  const isLessonUnlocked = useCallback((lessonId) => {
    if (lessonId === 1) return true;
    return progress.completedLessons.includes(lessonId - 1) && 
           (progress.lessonAccuracies[lessonId - 1] || 0) >= 60;
  }, [progress.completedLessons, progress.lessonAccuracies]);

  // Check if a lesson is completed
  const isLessonCompleted = useCallback((lessonId) => {
    return progress.completedLessons.includes(lessonId);
  }, [progress.completedLessons]);

  // Get lesson accuracy
  const getLessonAccuracy = useCallback((lessonId) => {
    return progress.lessonAccuracies[lessonId] || 0;
  }, [progress.lessonAccuracies]);

  // Reset all progress
  const resetProgress = useCallback(() => {
    setProgress(initialProgress);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    progress,
    isLoaded,
    completeOnboarding,
    completeLesson,
    isLessonUnlocked,
    isLessonCompleted,
    getLessonAccuracy,
    resetProgress
  };
};
